import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MessageCircle } from 'lucide-react';
import ConversationList from '../../components/Messages/ConversationList';
import ChatWindow from '../../components/Messages/ChatWindow';
import clientMessageService from '../../services/clientMessageService';
import './MessagesPage.css';

const POLL_MS = 15000;

const MessagesPage = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const selectedRef = useRef(null);

  useEffect(() => {
    selectedRef.current = selectedConversation;
  }, [selectedConversation]);

  const fetchConversations = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const data = await clientMessageService.getMyConversations();
      if (data.success) setConversations(data.conversations || []);
    } catch (error) {
      console.error('Erreur chargement conversations:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const refreshOpenMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;
    try {
      const data = await clientMessageService.getConversationMessages(conversationId);
      if (data.success) {
        setMessages(data.messages || []);
        await clientMessageService.markAsRead(conversationId);
      }
    } catch (error) {
      console.error('Erreur refresh messages:', error);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    const tick = () => {
      fetchConversations({ silent: true });
      const open = selectedRef.current;
      if (open) refreshOpenMessages(open.id);
    };
    const id = setInterval(tick, POLL_MS);
    return () => clearInterval(id);
  }, [fetchConversations, refreshOpenMessages]);

  const handleSelectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    setLoadingMessages(true);
    try {
      const data = await clientMessageService.getConversationMessages(conversation.id);
      if (data.success) setMessages(data.messages || []);
      await clientMessageService.markAsRead(conversation.id);
      await fetchConversations({ silent: true });
    } catch (error) {
      console.error('Erreur chargement messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSend = async (content) => {
    if (!selectedConversation) return;
    setSending(true);
    try {
      const data = await clientMessageService.sendMessage(selectedConversation.salon_id, content);
      if (data.success) {
        setMessages((prev) => [...prev, data.message]);
        await fetchConversations({ silent: true });
      }
    } catch (error) {
      console.error('Erreur envoi message:', error);
    } finally {
      setSending(false);
    }
  };

  const unreadTotal = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  return (
    <div className="messages-page">
      <div className="messages-page-header">
        <div className="messages-page-title-wrap">
          <span className="messages-page-icon">
            <MessageCircle size={20} strokeWidth={2.2} />
          </span>
          <div>
            <h1>Messages</h1>
            <p className="messages-page-sub">
              {conversations.length === 0
                ? 'Vos échanges avec les salons'
                : `${conversations.length} conversation${conversations.length > 1 ? 's' : ''}${
                    unreadTotal > 0 ? ` · ${unreadTotal} non lu${unreadTotal > 1 ? 's' : ''}` : ''
                  }`}
            </p>
          </div>
        </div>
      </div>

      <div className="messages-page-layout">
        <div className={`messages-page-sidebar ${selectedConversation ? 'hidden-mobile' : ''}`}>
          <ConversationList
            conversations={conversations}
            selectedId={selectedConversation?.id}
            onSelect={handleSelectConversation}
            loading={loading}
          />
        </div>

        <div className={`messages-page-chat ${!selectedConversation ? 'hidden-mobile' : ''}`}>
          <ChatWindow
            conversation={selectedConversation}
            messages={messages}
            loading={loadingMessages}
            sending={sending}
            onSend={handleSend}
            onBack={() => {
              setSelectedConversation(null);
              setMessages([]);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
